import {
  getDefaultBackgroundForModel,
  getModelById,
  type ModelDefaultAppearance
} from './modelRegistry';

export interface AvatarRuntimeConfig {
  modelId?: string;
  avatarModelId?: string;
  appearance?: Record<string, unknown>;
  visual?: Record<string, unknown>;
  animations?: Record<string, unknown>;
  sentiment?: Record<string, unknown>;
  moods?: Record<string, unknown>;
  faceCustomization?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface NormalizeAvatarRuntimeConfigOptions {
  modelId?: string | null;
  applyModelDefaults?: boolean;
  applyDefaultBackground?: boolean;
}

export interface MergeAvatarRuntimeConfigOptions extends NormalizeAvatarRuntimeConfigOptions {
  savedConfig?: Record<string, unknown> | null;
  hostOverrides?: Record<string, unknown> | null;
}

export interface ReplayAvatarRuntimeConfigOptions {
  target?: Window;
  includeVisualUpdate?: boolean;
  includeFaceCustomization?: boolean;
  includeMoods?: boolean;
  avatarInstanceId?: string;
  /**
   * Bypass the per-instance dedup. By default `replayAvatarRuntimeConfig`
   * skips the dispatch entirely when called twice in a row with the same
   * config for the same `avatarInstanceId` — this prevents the well-known
   * "3 backgrounds flash by on mount" bug where `CamlinAvatar`,
   * `App.tsx`, and `useHostedLiveControllerEffects` all replay the same
   * config back-to-back. Set `forceDispatch: true` for cases that
   * deliberately need to re-fire the events (e.g. a recovery path after
   * the GL context was lost — see `AvatarSceneShell` `webglcontextrestored`).
   */
  forceDispatch?: boolean;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : undefined;
}

function hasValues(value: Record<string, unknown> | undefined): value is Record<string, unknown> {
  return Boolean(value && Object.keys(value).length > 0);
}

function hasExplicitBackgroundImage(backgroundImage: unknown): backgroundImage is string {
  return typeof backgroundImage === 'string' && backgroundImage.trim().length > 0;
}

function resolveConfigModelId(config: Record<string, unknown>, explicitModelId?: string | null): string | undefined {
  if (explicitModelId) return explicitModelId;
  const avatarModelId = typeof config.avatarModelId === 'string' ? config.avatarModelId.trim() : '';
  if (avatarModelId) return avatarModelId;
  const modelId = typeof config.modelId === 'string' ? config.modelId.trim() : '';
  return modelId || undefined;
}

function preferredBackgroundAppearance(modelId?: string): Record<string, unknown> {
  const background = getDefaultBackgroundForModel(modelId);
  return {
    backgroundType: 'image',
    backgroundImage: background.variants?.desktop ?? background.url
  };
}

function normalizeAppearanceAliases(
  config: Record<string, unknown>,
  baseAppearance: Record<string, unknown>
): Record<string, unknown> {
  const appearance = { ...baseAppearance };

  for (const key of ['skinColor', 'eyeColor', 'hairColor', 'clothingColor', 'backgroundType', 'backgroundColor', 'backgroundImage']) {
    if (appearance[key] == null && typeof config[key] === 'string') {
      appearance[key] = config[key];
    }
  }
  if (appearance.clothingColor == null && typeof config.shirtColor === 'string') {
    appearance.clothingColor = config.shirtColor;
  }

  return appearance;
}

/**
 * Returns true when `input` carries any meaningful runtime-config data —
 * i.e. an `appearance` block with at least one key, OR any of the other
 * known top-level sections (visual / animations / sentiment / moods /
 * faceCustomization) with at least one key, OR any explicit
 * appearance-alias key (skinColor / hairColor / backgroundImage / …).
 *
 * Why this matters: `mergeAvatarRuntimeConfig({ savedConfig: null,
 * modelId, hostOverrides })` is called eagerly by `useAvatarRuntimeConfig`
 * while the saved config is still being fetched. Without this guard,
 * the empty input would still fall through `applyDefaultBackground` and
 * fabricate a *registry default* background URL, which the consumer
 * (`App.tsx` / `CamlinAvatar`) would then dispatch via `replay`. By the
 * time the real saved config arrives a second dispatch flips the
 * background to the saved one — visible to the user as "two backgrounds
 * flash by". Treating "no input data" as "do nothing yet" suppresses the
 * intermediate dispatch entirely.
 */
function hasMeaningfulRuntimeConfigInput(config: Record<string, unknown>): boolean {
  const appearance = asRecord(config.appearance);
  if (hasValues(appearance)) return true;
  for (const key of ['visual', 'animations', 'sentiment', 'moods', 'faceCustomization'] as const) {
    if (hasValues(asRecord(config[key]))) return true;
  }
  for (const key of ['skinColor', 'eyeColor', 'hairColor', 'clothingColor', 'shirtColor', 'backgroundType', 'backgroundColor', 'backgroundImage']) {
    if (typeof config[key] === 'string' && (config[key] as string).trim().length > 0) return true;
  }
  return false;
}

export function normalizeAvatarRuntimeConfig(
  input?: Record<string, unknown> | null,
  options: NormalizeAvatarRuntimeConfigOptions = {}
): AvatarRuntimeConfig | null {
  const config = asRecord(input);
  if (!config) return null;

  const modelId = resolveConfigModelId(config, options.modelId);
  const modelDefaults = options.applyModelDefaults !== false && modelId
    ? (getModelById(modelId)?.defaultAppearance ?? {}) as ModelDefaultAppearance
    : {};
  const rawAppearance = asRecord(config.appearance) ?? {};
  const appearance = normalizeAppearanceAliases(config, {
    ...modelDefaults,
    ...rawAppearance
  });

  const inputHasRealData = hasMeaningfulRuntimeConfigInput(config);
  const backgroundType = typeof appearance.backgroundType === 'string' ? appearance.backgroundType : undefined;
  if (
    options.applyDefaultBackground !== false
    && inputHasRealData
    && backgroundType !== 'solid'
    && backgroundType !== 'gradient'
    && !hasExplicitBackgroundImage(appearance.backgroundImage)
  ) {
    Object.assign(appearance, preferredBackgroundAppearance(modelId));
  }

  const normalized: AvatarRuntimeConfig = {
    ...config,
    modelId: typeof config.modelId === 'string' ? config.modelId : modelId,
    avatarModelId: typeof config.avatarModelId === 'string' ? config.avatarModelId : modelId
  };

  // Only retain appearance when input carried real data OR the caller
  // explicitly disabled model-default fallback (in which case anything we
  // produced was deliberate). This keeps "empty input" → "null output"
  // for the loading-spinner phase of useAvatarRuntimeConfig — see
  // hasMeaningfulRuntimeConfigInput's docstring.
  if (hasValues(appearance) && (inputHasRealData || options.applyModelDefaults === false)) {
    normalized.appearance = appearance;
  }
  for (const key of ['visual', 'animations', 'sentiment', 'moods', 'faceCustomization'] as const) {
    const section = asRecord(config[key]);
    if (hasValues(section)) normalized[key] = section;
  }

  // If after the above we have nothing but identifiers and no real
  // payload sections, return null so consumers can treat this as "no
  // config available yet" rather than dispatching a meaningless update.
  const hasPayload = Boolean(
    normalized.appearance
    || normalized.visual
    || normalized.animations
    || normalized.sentiment
    || normalized.moods
    || normalized.faceCustomization
  );
  if (!hasPayload) return null;

  return Object.keys(normalized).some(key => normalized[key] !== undefined) ? normalized : null;
}

function mergeRecordSection(
  base: Record<string, unknown> | undefined,
  override: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  const merged = {
    ...(base ?? {}),
    ...(override ?? {})
  };
  return hasValues(merged) ? merged : undefined;
}

export function mergeAvatarRuntimeConfig(
  options: MergeAvatarRuntimeConfigOptions = {}
): AvatarRuntimeConfig | null {
  const base = normalizeAvatarRuntimeConfig(options.savedConfig ?? {}, {
    modelId: options.modelId,
    applyModelDefaults: options.applyModelDefaults,
    applyDefaultBackground: options.applyDefaultBackground
  });
  const overrides = normalizeAvatarRuntimeConfig(options.hostOverrides ?? null, {
    modelId: options.modelId,
    applyModelDefaults: false,
    applyDefaultBackground: false
  });

  if (!base && !overrides) return null;
  if (!base) return overrides;
  if (!overrides) return base;

  const merged: AvatarRuntimeConfig = {
    ...base,
    ...overrides,
    modelId: overrides.modelId ?? base.modelId,
    avatarModelId: overrides.avatarModelId ?? base.avatarModelId
  };

  for (const key of ['appearance', 'visual', 'animations', 'sentiment', 'moods', 'faceCustomization'] as const) {
    const section = mergeRecordSection(
      asRecord(base[key]),
      asRecord(overrides[key])
    );
    if (section) merged[key] = section;
  }

  return merged;
}

const BACKGROUND_APPEARANCE_KEYS = ['backgroundType', 'backgroundColor', 'backgroundImage'] as const;

/**
 * Pull the background-related appearance keys out of a config-like object.
 * Accepts either a flat appearance record (the `initialAppearance` prop
 * shape, background keys at top level) or a full runtime config (the
 * `initialConfig` shape, background keys under `appearance`). When both
 * carry a key, the nested `appearance` section wins — it is the
 * saved-config shape and outranks flat aliases everywhere else in this
 * module (see `normalizeAppearanceAliases`).
 *
 * Returns `null` when the input carries no background keys at all, so
 * callers can distinguish "host said nothing about background" from
 * "host asked for a background".
 */
export function extractBackgroundAppearance(
  input?: Record<string, unknown> | null
): Record<string, string> | null {
  const config = asRecord(input);
  if (!config) return null;

  const appearance = asRecord(config.appearance);
  const result: Record<string, string> = {};
  for (const key of BACKGROUND_APPEARANCE_KEYS) {
    const value = appearance?.[key] ?? config[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      result[key] = value;
    }
  }

  return Object.keys(result).length > 0 ? result : null;
}

export type BackgroundUpdate =
  | { type: 'solid'; color: string }
  | { type: 'gradient'; color?: string }
  | { type: 'image'; imageUrl: string }
  | { type: 'none' };

/**
 * Interpret background appearance fields into the concrete update the
 * Scene `Bg` component should apply. This is the single source of truth
 * for the branch logic that used to live inline in Bg's
 * `avatar:config:update` listener — Bg now uses it for both the event
 * path and the `initialBackground` prop seed, so the two paths cannot
 * drift.
 *
 * Returns `null` when the record carries no background fields at all
 * (callers should keep their no-config fallbacks armed), and
 * `{ type: 'none' }` when background fields are present but nothing is
 * renderable (e.g. `backgroundType: 'transparent'`) — authoritative
 * truth that should suppress fallbacks without painting anything.
 */
export function resolveBackgroundUpdate(
  appearance?: Record<string, unknown> | null
): BackgroundUpdate | null {
  const record = asRecord(appearance);
  if (!record) return null;

  const backgroundType = typeof record.backgroundType === 'string' ? record.backgroundType : undefined;
  const backgroundColor = typeof record.backgroundColor === 'string' ? record.backgroundColor : undefined;
  const backgroundImage = hasExplicitBackgroundImage(record.backgroundImage)
    ? record.backgroundImage
    : undefined;

  if (!backgroundType && !backgroundColor && !backgroundImage) {
    return null;
  }

  if (backgroundType === 'solid' && backgroundColor) {
    return { type: 'solid', color: backgroundColor };
  }

  if (backgroundType === 'gradient') {
    // Scene backgrounds can't render CSS gradients; approximate with the
    // first hex stop (same behaviour the Bg listener always had).
    const gradientMatch = backgroundColor?.match(/#[0-9a-fA-F]{6}/);
    return { type: 'gradient', color: gradientMatch?.[0] };
  }

  if (backgroundImage) {
    return { type: 'image', imageUrl: backgroundImage };
  }

  return { type: 'none' };
}

/**
 * Per-instance dedup cache. Keyed by `avatarInstanceId` (or a singleton
 * `__default__` when the caller didn't pass one). Each entry stores the
 * stable JSON snapshot of the LAST config that was actually dispatched
 * for that instance. A subsequent replay with a structurally identical
 * config is a no-op.
 *
 * Why a Map and not WeakMap: `avatarInstanceId` is a string, not an
 * object reference. We rely on `clearReplayDedupForInstance` (called
 * from `Avatar.tsx`'s unmount path via `clearReplayDedup`) to keep this
 * Map from growing unbounded across long-running sessions with many
 * avatar mount/unmount cycles. The cache cap below is a belt-and-braces
 * fallback in case a consumer forgets to call the cleanup hook.
 */
const REPLAY_DEDUP_CACHE_CAP = 32;
const replayDedupCache = new Map<string, string>();

function snapshotConfigForDedup(config: AvatarRuntimeConfig): string {
  // Stable stringify by sorting keys at every level — JSON.stringify's
  // ordering depends on insertion order which can vary by call site,
  // so two structurally-identical configs could otherwise serialise to
  // different strings and miss the dedup.
  const sortKeys = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(sortKeys);
    if (value && typeof value === 'object') {
      return Object.keys(value as Record<string, unknown>).sort().reduce<Record<string, unknown>>((acc, key) => {
        acc[key] = sortKeys((value as Record<string, unknown>)[key]);
        return acc;
      }, {});
    }
    return value;
  };
  return JSON.stringify(sortKeys(config));
}

function dedupKeyForInstance(avatarInstanceId?: string): string {
  return avatarInstanceId && avatarInstanceId.trim().length > 0 ? avatarInstanceId : '__default__';
}

/**
 * Drop the dedup snapshot for an avatar instance. Call this from
 * unmount paths so the next mount gets a fresh dispatch even when the
 * config is unchanged from the previous mount.
 */
export function clearReplayDedupForInstance(avatarInstanceId?: string): void {
  replayDedupCache.delete(dedupKeyForInstance(avatarInstanceId));
}

/** Clear ALL dedup snapshots (test helper / global hard reset). */
export function clearAllReplayDedup(): void {
  replayDedupCache.clear();
}

export function replayAvatarRuntimeConfig(
  input?: Record<string, unknown> | null,
  options: ReplayAvatarRuntimeConfigOptions = {}
): void {
  const target = options.target ?? (typeof window !== 'undefined' ? window : undefined);
  if (!target) return;

  const config = normalizeAvatarRuntimeConfig(input);
  if (!config) return;

  if (!options.forceDispatch) {
    const key = dedupKeyForInstance(options.avatarInstanceId);
    const snapshot = snapshotConfigForDedup(config);
    if (replayDedupCache.get(key) === snapshot) {
      // Identical config already dispatched for this instance — skip
      // the entire chain. This is the fix for the "3 backgrounds flash
      // by" symptom: CamlinAvatar / App.tsx / useHostedLiveControllerEffects
      // all replay the same config back-to-back during initial mount,
      // and each re-dispatch was triggering a fresh texture load in
      // Scene/Bg.
      return;
    }
    replayDedupCache.set(key, snapshot);
    // Belt-and-braces: bound the cache so a consumer that forgets to
    // call clearReplayDedupForInstance can't grow this unboundedly.
    if (replayDedupCache.size > REPLAY_DEDUP_CACHE_CAP) {
      const oldestKey = replayDedupCache.keys().next().value;
      if (oldestKey !== undefined) replayDedupCache.delete(oldestKey);
    }
  }

  target.dispatchEvent(new CustomEvent('avatar:config:update', {
    detail: { config, appearance: config.appearance, avatarInstanceId: options.avatarInstanceId }
  }));

  if (options.includeVisualUpdate !== false && config.visual) {
    target.dispatchEvent(new CustomEvent('avatar:visual:update', {
      detail: { visual: config.visual, avatarInstanceId: options.avatarInstanceId }
    }));
  }

  if (options.includeFaceCustomization !== false && config.faceCustomization) {
    target.dispatchEvent(new CustomEvent('avatar:face:customize', {
      detail: config.faceCustomization
    }));
  }

  if (options.includeMoods !== false && config.moods) {
    target.dispatchEvent(new CustomEvent('avatar:moods:configure', {
      detail: { overrides: config.moods }
    }));
  }
}
